/**
 * A7Y-1083 — Plausibility judge sentinels and shared types.
 *
 * The plausibility judge runs after every subengine completes and inspects the
 * unified output for self-contradiction, fabrication, or impossible values
 * (see `a7y-functions/src/aso-engines/common/plausibility-judge.ts`). When the
 * judge fires a flag with severity at or above the gating threshold the audit
 * engine mutates the affected leaf into a {@link DataQualityFlag} sentinel so
 * the user-facing surface renders an "Unavailable — data quality check failed"
 * placeholder instead of the raw value. The raw value is preserved in the
 * engineering envelope (see `PlausibilityQuarantine.original_values`).
 */
import type { AsoEngineType } from './aso.js';

/**
 * Severity classification for an individual flagged value emitted by the
 * plausibility judge. The first three values (`fabricated`, `contradicts`,
 * `impossible`) gate publication; `low_confidence` is logged only.
 */
export type PlausibilityFlagSeverity =
  | 'fabricated'
  | 'contradicts'
  | 'impossible'
  | 'low_confidence';

/**
 * Severities that block publication of the affected fields. Mirrors the
 * gating policy described in the A7Y-1083 implementation plan §4.
 */
export const PLAUSIBILITY_GATING_SEVERITIES: readonly PlausibilityFlagSeverity[] = [
  'fabricated',
  'contradicts',
  'impossible',
];

/**
 * Single flagged value as classified by the deterministic regex classifier.
 *
 * - `raw` is the original free-form string from the judge.
 * - `field_paths` are JSON-path tokens extracted from the raw message (array
 *   indices normalized to `[*]`).
 * - `module_ids` are V2 module identifiers derived via
 *   {@link PLAUSIBILITY_ENGINE_QUARANTINE_MAP} when path extraction fails or
 *   when the engine map promotes the field to module-level suppression.
 */
export interface ClassifiedPlausibilityFlag {
  raw: string;
  severity: PlausibilityFlagSeverity;
  field_paths: string[];
  module_ids: string[];
}

/**
 * Output of the classifier wrapper around the raw judge response. Attached to
 * `SubengineOutput.plausibility_judge` after `runAuditPlausibilityJudges`.
 */
export interface ClassifiedPlausibilityJudgeResult {
  engine: AsoEngineType;
  audit_id?: string;
  status: 'passed' | 'flagged' | 'error';
  confidence: number | null;
  threshold: number;
  flags: ClassifiedPlausibilityFlag[];
  /**
   * True when any flag's severity is included in
   * {@link PLAUSIBILITY_GATING_SEVERITIES}, OR when `status === 'error'`
   * (A7Y-2305 — a judge that could not return a verdict fails closed rather
   * than shipping unvalidated values as an empty, non-blocking result). The
   * audit-engine gate uses this as the trigger for quarantine.
   *
   * A7Y-2421 — an error verdict is quarantined, never aborted: the gate
   * withholds that engine's declared values and collapses its V2 modules while
   * the remaining engines publish normally. It is also the flag the V2
   * transformer's `quarantineCoversModule` checks before collapsing a module.
   */
  blocks_publication: boolean;
  reasoning: string;
}

/**
 * Sentinel inserted in-place of a leaf value that failed the plausibility
 * gate. UI components branch on {@link isDataQualityFlag} to render the
 * placeholder badge.
 */
export interface DataQualityFlag {
  __dataQualityFlag: {
    severity: PlausibilityFlagSeverity;
    reason: string;
    engine: AsoEngineType;
  };
}

/**
 * Runtime guard. Narrow `unknown` (or a widened `T | DataQualityFlag`) to a
 * {@link DataQualityFlag}.
 */
export function isDataQualityFlag(value: unknown): value is DataQualityFlag {
  return (
    typeof value === 'object' &&
    value !== null &&
    '__dataQualityFlag' in value
  );
}

/**
 * Construct a {@link DataQualityFlag} sentinel.
 */
export function makeDataQualityFlag(
  severity: PlausibilityFlagSeverity,
  reason: string,
  engine: AsoEngineType,
): DataQualityFlag {
  return {
    __dataQualityFlag: {
      severity,
      reason,
      engine,
    },
  };
}

/**
 * Discriminator helper. Returns `{ value, flag: null }` when `v` is a normal
 * value, or `{ value: null, flag }` when `v` is a {@link DataQualityFlag}.
 */
export function getValueOrFlag<T>(
  v: T | DataQualityFlag,
): {
  value: T | null;
  flag: DataQualityFlag['__dataQualityFlag'] | null;
} {
  if (isDataQualityFlag(v)) {
    return { value: null, flag: v.__dataQualityFlag };
  }
  return { value: v as T, flag: null };
}

/**
 * Engineering-envelope record of a single quarantine application. Attached as
 * a sibling of `SubengineOutput.plausibility_judge` so downstream tooling can
 * audit which fields were redacted and recover the original values for
 * debugging.
 */
export interface PlausibilityQuarantine extends ClassifiedPlausibilityJudgeResult {
  /** JSON paths (resolved, indices expanded) that were rewritten to sentinels. */
  applied_paths: string[];
  /** Original leaf values that were replaced, keyed by their resolved path. */
  original_values: Array<{ path: string; value: unknown }>;
}
